import { motion } from "framer-motion";

import {
  Button,
  FloatingLabelInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Separator,
} from "@/components";
import { config } from "@/config/config";
import { useI18n } from "@/context/i18n";

import { ForgotPassword, TwoFactor } from "./components";
import { useLogin } from "./login.hooks";

export const Login: React.FC = () => {
  const { t } = useI18n();
  const {
    handleLogin,
    form,
    handleForgotPassword,
    forgotPasswordDialogOpen,
    onOpenForgotPasswordDialog,
    forgotPasswordForm,
    twoFactorForm,
    onSubmitTwoFactor,
    onCancelTwoFactor,
    twoFactorRequired,
  } = useLogin();
  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md space-y-6 bg-background/80 p-8 shadow-lg border border-muted/20"
      >
        {!twoFactorRequired ? (
          <>
            <div className="flex flex-col items-center space-y-2 text-center">
              <div className="w-24 h-24 bg-menu-item/5 rounded-full flex items-center justify-center">
                <img src={config.logoUrl} alt={config.brandName} className="w-18 h-18" />
              </div>
              <h1 className="text-2xl font-bold">{t("auth.login")}</h1>
              <p className="text-sm text-muted-foreground">
                {t("auth.enterAccount")}
              </p>
            </div>

            <div className="flex items-center">
              <Separator className="flex-1" />
              <span className="px-3 text-xs text-muted-foreground">
                {t("auth.emailLogin")}
              </span>
              <Separator className="flex-1" />
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleLogin)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("auth.email")}
                          type="email"
                          placeholder={t("generic.yourEmail")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormControl>
                            <FloatingLabelInput
                              label={t("auth.password")}
                              type="password"
                              placeholder="••••••••"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => onOpenForgotPasswordDialog(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      {t("auth.forgotPassword")}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full">
                  {t("auth.login")}
                </Button>
              </form>
            </Form>
          </>
        ) : (
          <TwoFactor
            twoFactorForm={twoFactorForm}
            onSubmit={onSubmitTwoFactor}
            onCancel={onCancelTwoFactor}
          />
        )}
      </motion.div>
      <ForgotPassword
        open={forgotPasswordDialogOpen}
        onOpenChange={onOpenForgotPasswordDialog}
        form={forgotPasswordForm}
        handleForgotPassword={handleForgotPassword}
      />
    </div>
  );
};
